// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// O mapa — grelha virtualizada de 250 viaturas × 365 dias
// ============================================================================
//
// A ESTRUTURA É REQUISITO; A ESTÉTICA É NOSSA (decisão do cliente, 2026-08-04)
//   Viaturas em linhas, dias em colunas, três bandas por viatura, sigla do cliente
//   visível, dia de rotação evidente. Isto não muda: é o que as planeadoras
//   conhecem, e a fonte 6 valoriza explicitamente a gestão da mudança.
//
// AS TRÊS BANDAS
//   1 · barra do itinerário — cliente, escrito UMA vez por corrida
//   2 · localidade do dia   — muda todos os dias, por isso é por dia
//   3 · barra do motorista  — nome, escrito UMA vez por corrida
//
//   ⚠️ As bandas têm **calha** entre si e a linha tem **separador**. O cliente
//   apontou que «os motoristas sobrepõem as restantes linhas»: as bandas estavam
//   encostadas e a barra de motorista de uma linha ficava colada à barra de
//   itinerário da seguinte, pelo que o olho as agrupava como a mesma viatura.
//
//   As corridas de itinerário e de motorista **não coincidem**, e é isso que a
//   banda própria mostra: a troca a meio do itinerário (`trsf`) parte uma e não a
//   outra. É o modelo operacional do negócio — a viatura fica fora e os motoristas
//   rodam.
//
// COR EM TUDO POR OMISSÃO
//   A pedido do cliente. A contrapartida está exposta: com ~40 clientes a cor
//   **não identifica** — a sigla identifica. A cor serve para varrer o mapa. O
//   filtro tem tudo ligado; o que se desliga fica **esbatido**, não escondido,
//   para nunca parecer que a viatura está livre quando não está.
//
// ÍCONES DE COR ÚNICA, NUNCA EMOJI
//   Ver o cabeçalho de `icones.tsx`. Emoji trazem cor própria para dentro de um
//   mapa onde a cor tem significado, mudam de forma por sistema, e desalinham em
//   texto de 9 px.
//
// VIRTUALIZAÇÃO — decidida por MEDIÇÃO, não por opinião (ADR-001)
//   273 750 células de uma vez são ~97× mais lentas do que a janela visível.
//
// PORQUE NÃO HÁ SINCRONIZAÇÃO DE SCROLL
//   Um só contentor com scroll e cabeçalhos `position: sticky`. O padrão dos
//   quatro painéis com `scrollLeft` copiado à mão introduz um atraso de um quadro
//   entre cabeçalho e corpo, visível como tremor.
// ============================================================================

import { BANDEIRA, type DadosMapa } from '@xlv-gva-mpt/dominio';
import {
  memo,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as EventoPonteiro,
  type MouseEvent as EventoRato,
} from 'react';
import { CONFIGURACAO_OMISSAO, type ConfiguracaoMapa } from './configuracao.js';
import { Pedidos } from './Pedidos.js';
import {
  detalhe,
  meioComIcone,
  resumo,
  resumoRotacao,
  rotuloDia,
  rotuloItinerario,
  rotuloMotorista,
  nomeLocal,
} from './celula.js';
import {
  ALTURA_CABECALHO,
  ALTURA_LINHA,
  ALTURA_LINHA_MAX,
  ALTURA_LINHA_MIN,
  ALTURA_PAINEL,
  ALTURA_PAINEL_MAX,
  ALTURA_PAINEL_MIN,
  DIAS_SEMANA,
  LARGURA_DIA,
  LARGURA_DIA_MAX,
  LARGURA_DIA_MIN,
  LARGURA_TIRA,
  LARGURA_VIATURA,
  LARGURA_VIATURA_MAX,
  LARGURA_VIATURA_MIN,
  MESES,
  SEPARADOR_LINHA,
  acumular,
  bandas,
  calcularJanela,
  mesmaJanela,
  tamanho,
  type Janela,
} from './dimensoes.js';
import {
  IconeChegada,
  IconeChevron,
  IconeComboio,
  IconeDecoracao,
  IconeDupla,
  IconeFechar,
  IconeFerry,
  IconeInfo,
  IconeOficina,
  IconePaleta,
  IconePartida,
  IconePesquisa,
  IconeRotacao,
  IconeVazio,
  IconeVisto,
  IconeVoo,
  IconeLista,
} from './icones.js';
import { ESTADO, corCliente, tintaSobre, type Tema } from './paleta.js';
import { segmentos, segmentosMotorista } from './segmentos.js';
import { iniciosDeSemana } from './semana.js';

interface Posicao {
  readonly v: number;
  readonly d: number;
}

/**
 * O que o filtro esconde — ou melhor, **esbate**.
 *
 * Três eixos, porque foram os três que o cliente pediu: cliente, motorista e
 * itinerário. Um conjunto vazio significa «nenhum activo»; `null` significa
 * «todos», que é o estado inicial e o mais comum. Distinguir os dois evita ter de
 * enumerar 40 clientes, 312 motoristas ou 2 679 rotas só para dizer «tudo ligado».
 */
interface Filtro {
  readonly clientes: ReadonlySet<number> | null;
  readonly motoristas: ReadonlySet<number> | null;
  readonly rotas: ReadonlySet<number> | null;
}

const SEM_FILTRO: Filtro = { clientes: null, motoristas: null, rotas: null };

function activo(conjunto: ReadonlySet<number> | null, i: number): boolean {
  return conjunto === null || i < 0 || conjunto.has(i);
}

function contaFiltros(f: Filtro, dados: DadosMapa): number {
  let n = 0;
  if (f.clientes !== null && f.clientes.size !== dados.clientes.length) n++;
  if (f.motoristas !== null && f.motoristas.size !== dados.motoristas.length) n++;
  if (f.rotas !== null && f.rotas.size !== dados.itinerarios.length) n++;
  return n;
}

function classes(...partes: readonly (string | false | undefined)[]): string {
  return partes.filter((p): p is string => typeof p === 'string').join(' ');
}

function IconeDoMeio({ meio }: { meio: string }) {
  const k = meioComIcone(meio);
  if (k === 'voo') return <IconeVoo em={0.95} className="ic" />;
  if (k === 'comboio') return <IconeComboio em={0.95} className="ic" />;
  if (k === 'ferry') return <IconeFerry em={0.95} className="ic" />;
  return null;
}

// ---------------------------------------------------------------------------
// Uma linha de viatura
// ---------------------------------------------------------------------------

interface LinhaProps {
  readonly dados: DadosMapa;
  readonly viatura: number;
  readonly janela: Janela;
  readonly tema: Tema;
  readonly corLigada: boolean;
  readonly filtro: Filtro;
  readonly inicioSemana: readonly boolean[];
  /** Itinerário seleccionado por clique. Realça a corrida inteira. */
  readonly itinerarioForte: number;
  /**
   * Posições acumuladas dos dias — onde cada coluna começa, em píxeis.
   *
   * ⚠️ Entra nas dependências do `memo` **pela identidade**: o vector é reconstruído
   * quando alguma largura muda e mantém-se igual quando não muda. Sem isso, mudar
   * uma coluna não voltava a desenhar as linhas e as barras ficavam no sítio antigo
   * enquanto as colunas mudavam de largura por baixo delas. E com uma identidade
   * nova a cada renderização, passar o rato voltaria a desenhar a grelha inteira.
   */
  readonly offsetsDias: readonly number[];
  /** Altura desta linha. Cada viatura pode ter a sua, como no Excel. */
  readonly altura: number;
}

/**
 * `memo` aqui não é optimização prematura.
 *
 * É o que permite ao cartão de resumo seguir o cursor **sem** voltar a desenhar as
 * linhas. Sem ele, passar o rato pela grelha redesenhava tudo a cada célula.
 */
const Linha = memo(function Linha({
  dados,
  viatura,
  janela,
  tema,
  corLigada,
  filtro,
  inicioSemana,
  itinerarioForte,
  offsetsDias,
  altura,
}: LinhaProps) {
  const v = dados.viaturas[viatura];
  const linha = dados.grelha[viatura] ?? [];
  const movimentos = dados.movimentos[viatura] ?? [];
  const { dia0, dia1 } = janela;

  const segs = segmentos(linha, dia0, dia1);
  const segsMot = segmentosMotorista(linha, dia0, dia1);

  // ⚠️ Já não é uma multiplicação: cada coluna pode ter a sua largura, logo a
  // posição de um dia é a soma das larguras dos anteriores — que é o que o vector
  // acumulado traz pronto.
  const esq = (d: number): number => offsetsDias[d] ?? 0;
  const larg = (a: number, b: number): number => (offsetsDias[b] ?? 0) - (offsetsDias[a] ?? 0);
  const banda = bandas(altura);

  // ---- banda 1 e blocos de altura inteira --------------------------------
  const barras = [];
  for (const s of segs) {
    const estilo = { left: esq(s.dia0), width: larg(s.dia0, s.dia1) };

    if (s.tipo === 'rotacao') {
      const r = resumoRotacao(movimentos[s.dia0] ?? null);
      const cel = linha[s.dia0];
      // ⚠️ O bloco ocupa a altura inteira e TAPAVA a barra do motorista. E é
      // precisamente neste dia que o motorista muda — foi o cliente a dizê-lo:
      // «nos dias a preto pode haver troca de motorista a mudar o itinerário».
      // Por isso o bloco encolhe e deixa a banda 3 à vista.
      const mot = cel != null && cel[2] >= 0 ? (dados.motoristas[cel[2]] ?? '') : '';
      barras.push(
        <div key={`r${s.dia0}`} className="bloco bloco--rotacao" style={estilo}>
          <span className="bloco-hora">
            <IconeChegada em={0.9} />
            {r.chegada}
          </span>
          <span className="bloco-hora">
            <IconePartida em={0.9} />
            {r.partida}
          </span>
          {mot !== '' ? <span className="bloco-mot">{mot}</span> : null}
        </div>,
      );
      continue;
    }

    if (s.tipo === 'parado') {
      // Dia sem viagem, mas com local. Sem cor de cliente: não há cliente.
      //
      // ⚠️ DUAS coisas diferentes, e a diferença é dinheiro. Foi o cliente a
      // apontá-la: «muitas vezes, nos dias vazios, o local deve ser quando eles
      // vão a casa e não têm de pernoitar, por exemplo quando estão em Portugal».
      //
      // MEDIDO na folha real: das 240 células com local em dias sem viagem,
      // **nenhuma** é em Portugal — escrevem o local só quando a viatura NÃO está
      // em casa. Logo o branco da folha não era esquecimento, era «está na base».
      //   na base    → o motorista vai a casa, alojamento zero (D-060)
      //   fora       → alojamento pago, e é dos maiores custos controláveis
      const naBase = s.chave >= 0 && s.chave === dados.localidadeBase;
      barras.push(
        <div
          key={`p${s.dia0}`}
          className={classes(
            'barra',
            'barra--parado',
            naBase && 'barra--base',
            s.abre && 'abre',
            s.fecha && 'fecha',
          )}
          style={estilo}
          title={
            naBase
              ? 'Na base — sem serviço. O motorista vai a casa: não há alojamento a pagar.'
              : 'Parada fora da base — sem serviço, mas com pernoita a pagar.'
          }
        >
          <span className="barra-sigla">{naBase ? 'na base' : 'parada fora'}</span>
        </div>,
      );
      continue;
    }

    if (s.tipo === 'estado') {
      const cel = linha[s.dia0];
      const cor = ESTADO[dados.estados[cel?.[3] ?? 2] ?? 'grave'];
      barras.push(
        <div
          key={`e${s.dia0}`}
          className={classes('bloco', 'bloco--estado', s.abre && 'abre', s.fecha && 'fecha')}
          style={{ ...estilo, background: cor, color: tintaSobre(cor) }}
        >
          <IconeOficina em={1} className="ic" />
          <span className="bloco-rot">{dados.estadosViatura[s.chave] ?? ''}</span>
        </div>,
      );
      continue;
    }

    // Itinerário. O filtro ESBATE, nunca esconde: uma barra que desaparecesse
    // faria a viatura parecer livre num dia em que não está.
    const dentro =
      activo(filtro.clientes, s.cliente) &&
      activo(filtro.rotas, linha[s.dia0]?.[5] ?? -1) &&
      activo(filtro.motoristas, linha[s.dia0]?.[2] ?? -1);
    const cor = corLigada && dentro ? corCliente(s.cliente, tema) : corCliente(-1, tema);
    const dias = s.dia1 - s.dia0;
    const rot = rotuloItinerario(dados, s.cliente, dias);
    const forte = !Number.isNaN(itinerarioForte) && s.chave === itinerarioForte;

    // QUALQUER dia da corrida, e não apenas o primeiro.
    //
    // Nos dados reais o estado do registo é uniforme dentro de um itinerário, mas
    // ler só o primeiro dia esconderia uma reserva que cobrisse apenas parte da
    // corrida — e «parte deste itinerário não está confirmada» é exactamente o que
    // o planeador precisa de ver. O mesmo vale para o «?» de provisório.
    let bandeirasCorrida = 0;
    for (let d = s.dia0; d < s.dia1; d++) bandeirasCorrida |= linha[d]?.[4] ?? 0;
    const reserva = (bandeirasCorrida & BANDEIRA.RESERVA) !== 0;
    const duvida = (bandeirasCorrida & BANDEIRA.DUVIDA) !== 0;
    const suspeita = (bandeirasCorrida & BANDEIRA.ROTACAO_SUSPEITA) !== 0;

    barras.push(
      <div
        key={`i${s.dia0}`}
        className={classes(
          'barra',
          'barra--itin',
          s.abre && 'abre',
          s.fecha && 'fecha',
          reserva && 'reserva',
          forte && 'forte',
          suspeita && 'suspeita',
        )}
        style={{
          ...estilo,
          background: cor.fundo,
          color: tintaSobre(cor.fundo),
          borderLeftColor: cor.cap,
        }}
      >
        <span className="barra-sigla">{rot.sigla}</span>
        {/* Sem ícone de reserva na barra: a TEXTURA já a diz, e a legenda
            explica-a. Um ícone em 19 % das barras custava ~2 nós cada em 600
            dias visíveis, e o p95 nota-o. */}
        {duvida ? <span className="barra-duvida">?</span> : null}
        {suspeita ? (
          // Vinha marcado como rotação na origem, mas não há partida no dia
          // seguinte. Medido: 14,3 % dos dias marcados. Assinala-se em vez de se
          // corrigir em silêncio — na folha real é provavelmente erro de registo.
          <span className="barra-suspeita" title="marcado como rotação, sem partida no dia seguinte">
            !
          </span>
        ) : null}
        {rot.nome !== '' ? <span className="barra-nome">{rot.nome}</span> : null}
      </div>,
    );
  }

  // ---- banda 3 · corridas de motorista -----------------------------------
  const barrasMot = segsMot.map((s) => (
    <div
      key={`m${s.dia0}`}
      className={classes(
        'barra',
        'barra--mot',
        s.abre && 'abre',
        s.fecha && 'fecha',
        s.motorista2 >= 0 && 'dupla',
      )}
      style={{ left: esq(s.dia0), width: larg(s.dia0, s.dia1) }}
    >
      {s.motorista2 >= 0 ? <IconeDupla em={0.95} className="ic" /> : null}
      <span className="barra-mot-nome">
        {rotuloMotorista(dados, s.motorista, s.motorista2, s.dia1 - s.dia0)}
      </span>
    </div>
  ));

  // ---- dias: alvo de rato, localidade e ritmo da semana -------------------
  const dias = [];
  for (let d = dia0; d < dia1; d++) {
    const cel = linha[d] ?? null;
    const normal = cel !== null && cel[7] < 0 && (cel[4] & BANDEIRA.ROTACAO) === 0;
    const r = normal ? rotuloDia(dados, cel) : null;
    dias.push(
      <div
        key={d}
        className={classes(
          'dia',
          dados.fimDeSemana[d] === true && 'dia--fds',
          inicioSemana[d] === true && 'dia--semana',
          cel === null && 'dia--vazio',
        )}
        data-v={viatura}
        data-d={d}
        // Cada coluna tem a sua largura: vem no elemento, não de uma regra CSS.
        style={{ width: tamanho(offsetsDias, d) }}
      >
        {r !== null && r.local === '' ? (
          // ⚠️ Localidade em FALTA. Medido na folha real: 990 de 16 690 dias
          // dentro de uma corrida (5,9 %) não têm localidade. O cliente exige que
          // seja sempre definida — logo mostra-se a lacuna em vez de a esconder,
          // que é a única forma de alguém a preencher.
          // ⚠️ O `?` sozinho não se explicava — foi o cliente a dizê-lo: «não
          // percebi o ? em certos dias». Duas razões, e as duas eram nossas: o
          // mesmo glifo servia para «provisório» na banda 1 e para «local em
          // falta» na banda 2, e nenhum dos dois estava na legenda. Agora este
          // leva caixa tracejada — lê-se como campo por preencher, não como
          // dúvida — e ambos entraram em «Como ler o mapa».
          <span
            className="dia-local dia-local--falta"
            title="Local por definir. Nos dias sem chegada nem partida o local é onde o motorista dorme — e é o que determina se o repouso é legal (art. 8.º n.º 8) e se há alojamento a pagar."
          >
            ?
          </span>
        ) : null}
        {r !== null && r.local !== '' ? (
          <span className="dia-local">
            {r.sentido === 'chegada' ? <IconeChegada em={0.9} className="ic" /> : null}
            {r.sentido === 'partida' ? <IconePartida em={0.9} className="ic" /> : null}
            {/* DOIS locais no primeiro dia: onde os passageiros entram e onde se
                pernoita. Medido em 4,6 % das células de localidade da folha real.
                O de embarque vem primeiro e mais fraco — o que fica na linha é
                onde a viatura dorme. */}
            {r.embarque !== '' ? (
              <span className="dia-embarque" title="entrada dos passageiros">
                {r.embarque}/
              </span>
            ) : null}
            <span className="dia-local-txt">{r.local}</span>
            {cel !== null && cel[8] >= 0 ? (
              <IconeDoMeio meio={dados.meios[cel[8]] ?? ''} />
            ) : null}
          </span>
        ) : null}
      </div>,
    );
  }

  return (
    <div
      className="linha"
      // A altura e as três bandas são desta linha, não de todas: cada viatura pode
      // ter a sua. As bandas vêm calculadas em JS e não por `calc` no CSS porque em
      // `calc` davam sub-píxeis e as arestas das barras ficavam esfumadas.
      style={
        {
          height: altura,
          '--altura-util': `${altura - SEPARADOR_LINHA}px`,
          '--h-itin': `${banda.itinerario}px`,
          '--h-local': `${banda.localidade}px`,
          '--h-mot': `${banda.motorista}px`,
        } as React.CSSProperties
      }
    >
      {/* `data-linha` é o que o arrasto usa para saber QUAL linha redimensionar.
          Deliberadamente não é `data-v`: esse é o atributo das células de dia, e o
          ouvinte de clique procura `[data-v]` — pô-lo aqui faria o arrasto da altura
          seleccionar um dia inexistente. */}
      <div className="viatura" data-linha={viatura}>
        <span className="viatura-num">{v?.numero ?? ''}</span>
        <span className="viatura-mat">{v?.matricula ?? ''}</span>
        <span className="viatura-lot">{v?.lotacao ?? ''}</span>
        {/* CONDUTOR PRINCIPAL da viatura, como na folha do Excel — medido: 93 %
            das linhas de viatura reais trazem-no no cabeçalho (62 de 67).
            ⚠️ É METADADO, não atribuição: cobre a mediana de 44 % dos dias e às
            vezes nem é quem conduz mais. Quem conduz num dado dia é a barra da
            banda 3. Está aqui para ajudar a planear, não para dizer quem vai. */}
        {v !== undefined && v.motoristaPreferencial >= 0 ? (
          <span className="viatura-principal" title="condutor principal desta viatura">
            {dados.motoristas[v.motoristaPreferencial] ?? ''}
          </span>
        ) : null}
        {v !== undefined && v.clienteDecoracao >= 0 ? (
          // «Autocarro decorado Mercury» — medido na folha real, em linha própria.
          // Restringe que clientes podem usar a viatura, logo é do planeamento.
          <span className="viatura-dec">
            <IconeDecoracao em={0.85} />
            {dados.clientes[v.clienteDecoracao]?.[0] ?? ''}
          </span>
        ) : null}
      </div>
      <div className="faixa">
        <div className="dias" style={{ paddingLeft: esq(dia0) }}>
          {dias}
        </div>
        <div className="camada">{barras}</div>
        <div className="camada camada--mot">{barrasMot}</div>
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// O mapa
// ---------------------------------------------------------------------------

export interface MapaProps {
  readonly dados: DadosMapa;
  readonly configuracao?: ConfiguracaoMapa;
  /**
   * Janela imposta de fora, em vez de derivada do deslocamento.
   *
   * ⚠️ **NÃO é uma opção de utilização.** Existe por uma razão medida: o React 19
   * não reage a um evento `scroll` despachado por código — verificado com o
   * contentor dimensionado, a aritmética da janela correcta e o evento a chegar ao
   * elemento. Sem esta costura, a única forma de medir o mapa seria medir **uma
   * cópia** dele, que é exactamente o erro de medição já cometido neste projeto.
   */
  readonly janelaFixa?: Janela;
  /**
   * Volta a pedir o mapa ao servidor.
   *
   * ⚠️ Chamado depois de uma ESCRITA. Sem isto, o planeador reservava e a grelha ao
   * lado continuava a mostrar a viatura livre — a cache do servidor era invalidada,
   * mas o navegador já tinha os dados. Ver `App.tsx`.
   */
  readonly recarregar?: (() => void) | undefined;
}

export function Mapa({ dados, configuracao = CONFIGURACAO_OMISSAO, janelaFixa, recarregar }: MapaProps) {
  const caixa = useRef<HTMLDivElement>(null);
  const envolvente = useRef<HTMLDivElement>(null);
  const [janelaCalculada, setJanela] = useState<Janela>({
    viatura0: 0,
    viatura1: 0,
    dia0: 0,
    dia1: 0,
  });
  const janela = janelaFixa ?? janelaCalculada;

  const [sobre, setSobre] = useState<(Posicao & { x: number; y: number }) | null>(null);
  const [seleccao, setSeleccao] = useState<Posicao | null>(null);
  const [corLigada, setCorLigada] = useState(true);
  const [filtro, setFiltro] = useState<Filtro>(SEM_FILTRO);
  const [painelAberto, setPainelAberto] = useState(false);
  // ⚠️ Aqui e não no `Topo`: a janela de pedidos é irmã do mapa e não filha da barra.
  // Dentro da barra ficava presa ao seu contexto de empilhamento (`z-index: 20`) e o
  // cartão junto ao cursor (`z-index: 40`) pintava por cima dela.
  const [pedidosAbertos, setPedidosAbertos] = useState(false);
  const [larguraViatura, setLarguraViatura] = useState(LARGURA_VIATURA);
  const [alturaPainel, setAlturaPainel] = useState(ALTURA_PAINEL);
  // ---- medidas da grelha, uma a uma, como no Excel ------------------------
  //
  // ⚠️ A primeira versão redimensionava TODAS as colunas ao mesmo tempo. O cliente
  // experimentou e recusou: «não gostei da forma como se arrastam… tem de ser a
  // mesma dinâmica do Excel: ao arrastar uma coluna ou linha só interfere no
  // tamanho dessa linha, nunca em todas as outras».
  //
  // Guardam-se só as EXCEPÇÕES — quem foi arrastado. Um mapa vazio é a grelha
  // uniforme de sempre, e é o caso normal: com 365 dias, guardar 365 medidas iguais
  // para poder mudar duas seria desperdício e uma fonte de divergência.
  const [larguraDias, setLarguraDias] = useState<ReadonlyMap<number, number>>(new Map());
  const [alturaLinhas, setAlturaLinhas] = useState<ReadonlyMap<number, number>>(new Map());

  // Posições acumuladas. A identidade destes vectores é o que decide se as linhas
  // voltam a ser desenhadas — por isso `useMemo` com as excepções como dependência,
  // e por isso o arrasto é limitado a um quadro de animação.
  const offsetsDias = useMemo(
    () => acumular(dados.nDias, LARGURA_DIA, larguraDias),
    [dados.nDias, larguraDias],
  );
  const offsetsLinhas = useMemo(
    () => acumular(dados.viaturas.length, ALTURA_LINHA, alturaLinhas),
    [dados.viaturas.length, alturaLinhas],
  );

  /** Verdadeiro enquanto uma medida está a ser arrastada. Ver `aoPassar`. */
  const arrastando = useRef(false);
  const tema = useTema();

  // Nada filtrado por omissão — foi o pedido explícito do cliente: cor em tudo,
  // e o filtro só quando alguém o quiser.
  useEffect(() => {
    setFiltro(SEM_FILTRO);
  }, [dados]);

  const inicioSemana = useMemo(() => iniciosDeSemana(dados.fimDeSemana), [dados.fimDeSemana]);

  /**
   * A chave da corrida seleccionada, no mesmo espaco que `segmentos` usa.
   *
   * Corrida positiva, rota negativa a partir de -2 — ver `segmentos.ts`. Com os
   * indices em cru, a corrida 0 e a rota 0 eram a mesma chave e o realce acendia
   * barras sem relacao.
   */
  const itinerarioForte = useMemo(() => {
    if (seleccao === null) return Number.NaN;
    const c = dados.grelha[seleccao.v]?.[seleccao.d];
    if (c == null || c[7] >= 0) return Number.NaN;
    return c[10] >= 0 ? c[10] : -(c[5] + 2);
  }, [seleccao, dados.grelha]);

  // ---- medição em condições reais (risco R1 do ADR-001) -------------------
  const marca = useRef<number | null>(null);
  const quadros = useRef<number[]>([]);
  const primeiroMedido = useRef(false);
  const [p95, setP95] = useState<number | null>(null);

  /**
   * A janela também num `ref`, e não só no estado.
   *
   * ⚠️ Não é duplicação por descuido. A comparação tem de acontecer **antes** de
   * `setJanela`, para o cronómetro arrancar no momento do evento de scroll. Feita
   * dentro do *updater*, o React só a executa na renderização, e a medição excluía
   * o tempo de agendamento — que é parte do que o utilizador sente. Em
   * `StrictMode` o *updater* corre duas vezes, o que agravava o erro.
   */
  const janelaRef = useRef<Janela>(janela);

  const recalcular = useCallback(() => {
    if (janelaFixa !== undefined) return;
    const el = caixa.current;
    if (el === null) return;
    const nova = calcularJanela(
      { topo: el.scrollTop, esquerda: el.scrollLeft },
      { altura: el.clientHeight, largura: el.clientWidth },
      { viaturas: dados.viaturas.length, dias: dados.nDias },
      { larguraViatura, dias: offsetsDias, linhas: offsetsLinhas },
    );
    if (mesmaJanela(janelaRef.current, nova)) return;
    janelaRef.current = nova;
    marca.current = performance.now();
    setJanela(nova);
  }, [dados.viaturas.length, dados.nDias, janelaFixa, larguraViatura, offsetsDias, offsetsLinhas]);

  // Redimensionar muda quantas linhas e colunas cabem, logo a janela tem de ser
  // recalculada. Sem isto, esticar a linha deixava metade do ecrã em branco até ao
  // próximo deslocamento — o conteúdo cresce, a janela desenhada não.
  useEffect(() => {
    recalcular();
  }, [recalcular]);

  // `useLayoutEffect` e não `useEffect`: corre antes de o navegador pintar, o que
  // permite forçar o layout e medir o custo real.
  useLayoutEffect(() => {
    if (marca.current === null) return;
    const el = caixa.current;
    if (el !== null) void el.offsetHeight;
    const decorrido = performance.now() - marca.current;
    marca.current = null;

    // Descartar a PRIMEIRA janela: vem do efeito de montagem, corre já dentro do
    // ciclo do React e mede quase zero. Incluí-la fazia a barra mostrar
    // «p95 0,0 ms» antes de haver qualquer deslocamento — um número tranquilizador
    // e sem significado, que é o pior tipo de número.
    if (!primeiroMedido.current) {
      primeiroMedido.current = true;
      return;
    }

    const q = quadros.current;
    q.push(decorrido);
    if (q.length > 200) q.shift();
    const ordenado = [...q].sort((a, b) => a - b);
    setP95(ordenado[Math.min(ordenado.length - 1, Math.floor(ordenado.length * 0.95))] ?? null);
  }, [janela]);

  useEffect(() => {
    recalcular();
    const el = caixa.current;
    if (el === null || typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(() => recalcular());
    observador.observe(el);
    return () => observador.disconnect();
  }, [recalcular]);

  // Delegação: um só ouvinte para todos os dias visíveis, em vez de um por dia.
  const aoPassar = useCallback((evento: EventoRato<HTMLDivElement>) => {
    // Durante um arrasto de medida o cartão de resumo não deve seguir o cursor: o
    // arrasto é justamente o caminho onde não se pode voltar a desenhar por pixel.
    if (arrastando.current) return;
    const alvo = (evento.target as HTMLElement).closest<HTMLElement>('[data-v]');
    if (alvo === null) {
      setSobre(null);
      return;
    }
    const v = Number(alvo.dataset['v']);
    const d = Number(alvo.dataset['d']);
    const r = alvo.getBoundingClientRect();
    setSobre((a) => (a?.v === v && a.d === d ? a : { v, d, x: r.left, y: r.bottom }));
  }, []);

  const aoSair = useCallback(() => setSobre(null), []);

  const aoClicar = useCallback((evento: EventoRato<HTMLDivElement>) => {
    const alvo = (evento.target as HTMLElement).closest<HTMLElement>('[data-v]');
    if (alvo === null) return;
    const v = Number(alvo.dataset['v']);
    const d = Number(alvo.dataset['d']);
    // Clicar outra vez no mesmo dia desmarca — senão não haveria como desmarcar.
    setSeleccao((a) => (a?.v === v && a.d === d ? null : { v, d }));
  }, []);

  // ---- redimensionar colunas e linhas, arrastando a borda -----------------
  //
  // Pedido do cliente: *«quero que seja possível, tal como acontece no Excel,
  // alargar ou apertar uma coluna ou linha, porque algumas estão muito apertadas e
  // não dá para ver tudo»*.
  //
  // ⚠️ **Uma coluna de cada vez, uma linha de cada vez** — como no Excel. A
  // primeira versão mexia em todas ao mesmo tempo e o cliente recusou-a depois de
  // experimentar. O que se guarda é a EXCEPÇÃO do índice arrastado.
  //
  // ⚠️ Um só par de ouvintes em `.mapa-conteudo`, por delegação, e o alvo
  // reconhece-se pela proximidade à borda — como numa folha de cálculo. Punhos
  // dedicados seriam ~57 nós por janela (40 dias + 17 linhas), ou seja ~2 % do
  // orçamento de nós gasto em punhos. As tiras são pseudo-elementos: dão o cursor
  // e o alvo de rato sem entrar na contagem.
  //
  // ⚠️ E aqui está a contrapartida que a versão global não tinha, declarada: com
  // medidas por índice a geometria deixa de ser uma multiplicação, logo **não há
  // variável CSS única para escrever durante o arrasto**. Mudar uma coluna desloca
  // todas as barras à sua direita, e essas posições são calculadas em JavaScript.
  // Por isso o arrasto passa pelo estado do React — mas **no máximo uma vez por
  // quadro de animação**, senão seria um redesenho por píxel de ponteiro.
  const arrasto = useRef<{
    eixo: 'x' | 'y';
    indice: number;
    pos: number;
    base: number;
  } | null>(null);
  const quadroPedido = useRef<number | null>(null);
  const porAplicar = useRef<{ eixo: 'x' | 'y'; indice: number; valor: number } | null>(null);

  /**
   * A borda debaixo do ponteiro, se houver: que eixo e **que índice**.
   *
   * O índice vem do `data-*` do elemento e não de contas com coordenadas: com
   * medidas por índice, deduzi-lo de píxeis seria repetir a busca binária num sítio
   * onde ela pode divergir da que desenha.
   */
  const bordaSob = (
    alvo: EventTarget | null,
    x: number,
    y: number,
  ): { eixo: 'x' | 'y'; indice: number } | null => {
    const el = alvo as HTMLElement | null;
    if (el === null) return null;
    const cab = el.closest<HTMLElement>('.dia-cab');
    if (cab !== null) {
      if (x < cab.getBoundingClientRect().right - LARGURA_TIRA) return null;
      return { eixo: 'x', indice: Number(cab.dataset['dia']) };
    }
    const via = el.closest<HTMLElement>('.viatura');
    if (via !== null) {
      if (y < via.getBoundingClientRect().bottom - LARGURA_TIRA) return null;
      return { eixo: 'y', indice: Number(via.dataset['linha']) };
    }
    return null;
  };

  /** Substitui uma medida, devolvendo o mesmo mapa se nada mudou. */
  const comMedida = (
    m: ReadonlyMap<number, number>,
    indice: number,
    valor: number,
  ): ReadonlyMap<number, number> => {
    if (m.get(indice) === valor) return m; // sem alteração, sem redesenho
    const novo = new Map(m);
    novo.set(indice, valor);
    return novo;
  };

  const aoDescerMedida = (evento: EventoPonteiro<HTMLDivElement>): void => {
    const borda = bordaSob(evento.target, evento.clientX, evento.clientY);
    if (borda === null || Number.isNaN(borda.indice)) return;
    evento.preventDefault();
    evento.currentTarget.setPointerCapture(evento.pointerId);
    arrasto.current = {
      eixo: borda.eixo,
      indice: borda.indice,
      pos: borda.eixo === 'x' ? evento.clientX : evento.clientY,
      base:
        borda.eixo === 'x'
          ? tamanho(offsetsDias, borda.indice)
          : tamanho(offsetsLinhas, borda.indice),
    };
    arrastando.current = true;
    setSobre(null);
  };

  const aoMoverMedida = (evento: EventoPonteiro<HTMLDivElement>): void => {
    const a = arrasto.current;
    if (a === null) return;
    const delta = (a.eixo === 'x' ? evento.clientX : evento.clientY) - a.pos;
    const [minimo, maximo] =
      a.eixo === 'x'
        ? [LARGURA_DIA_MIN, LARGURA_DIA_MAX]
        : [ALTURA_LINHA_MIN, ALTURA_LINHA_MAX];
    porAplicar.current = {
      eixo: a.eixo,
      indice: a.indice,
      valor: Math.round(Math.max(minimo, Math.min(maximo, a.base + delta))),
    };
    if (quadroPedido.current !== null) return; // já há um quadro à espera
    quadroPedido.current = requestAnimationFrame(() => {
      quadroPedido.current = null;
      const p = porAplicar.current;
      if (p === null) return;
      if (p.eixo === 'x') setLarguraDias((m) => comMedida(m, p.indice, p.valor));
      else setAlturaLinhas((m) => comMedida(m, p.indice, p.valor));
    });
  };

  const aoSubirMedida = (evento: EventoPonteiro<HTMLDivElement>): void => {
    if (arrasto.current === null) return;
    evento.currentTarget.releasePointerCapture(evento.pointerId);
    arrasto.current = null;
    arrastando.current = false;
    // O último movimento pode ter ficado num quadro que ainda não correu; aplicá-lo
    // aqui garante que o valor final é o que o ponteiro deixou, e não o penúltimo.
    if (quadroPedido.current !== null) {
      cancelAnimationFrame(quadroPedido.current);
      quadroPedido.current = null;
    }
    const p = porAplicar.current;
    porAplicar.current = null;
    if (p === null) return;
    if (p.eixo === 'x') setLarguraDias((m) => comMedida(m, p.indice, p.valor));
    else setAlturaLinhas((m) => comMedida(m, p.indice, p.valor));
  };

  useEffect(
    () => () => {
      if (quadroPedido.current !== null) cancelAnimationFrame(quadroPedido.current);
    },
    [],
  );

  /**
   * Duplo clique na borda devolve **essa** coluna ou linha à medida de omissão.
   *
   * Não é adorno: sem caminho de volta, quem arrasta até um extremo e não gosta
   * fica sem forma de reverter a não ser recarregar a página e perder o resto do
   * estado — filtros, selecção, painel.
   */
  const aoDuploCliqueMedida = (evento: EventoRato<HTMLDivElement>): void => {
    const borda = bordaSob(evento.target, evento.clientX, evento.clientY);
    if (borda === null || Number.isNaN(borda.indice)) return;
    const remover = (m: ReadonlyMap<number, number>): ReadonlyMap<number, number> => {
      if (!m.has(borda.indice)) return m;
      const novo = new Map(m);
      novo.delete(borda.indice);
      return novo;
    };
    if (borda.eixo === 'x') setLarguraDias(remover);
    else setAlturaLinhas(remover);
  };

  const larguraTotal = larguraViatura + (offsetsDias[dados.nDias] ?? 0);
  const alturaTotal = ALTURA_CABECALHO + (offsetsLinhas[dados.viaturas.length] ?? 0);

  const linhas = [];
  for (let v = janela.viatura0; v < janela.viatura1; v++) {
    linhas.push(
      <Linha
        key={v}
        dados={dados}
        viatura={v}
        janela={janela}
        tema={tema}
        corLigada={corLigada}
        filtro={filtro}
        inicioSemana={inicioSemana}
        itinerarioForte={itinerarioForte}
        offsetsDias={offsetsDias}
        altura={tamanho(offsetsLinhas, v)}
      />,
    );
  }

  const fixado = seleccao ?? (sobre === null ? null : { v: sobre.v, d: sobre.d });

  return (
    <div
      className="mapa-envolvente"
      ref={envolvente}
      style={
        {
          '--largura-viatura': `${larguraViatura}px`,
          '--altura-painel': `${painelAberto ? alturaPainel : 0}px`,
          // ⚠️ As medidas da grelha NÃO vivem aqui. Cada coluna e cada linha tem a
          // sua, logo vão no elemento respectivo — largura no `.dia`/`.dia-cab`,
          // altura e bandas no `.linha`. Uma variável só no topo era o que a versão
          // anterior tinha, e é exactamente o que fazia arrastar uma coluna mexer em
          // todas as outras.
        } as React.CSSProperties
      }
    >
      <Topo
        dados={dados}
        filtro={filtro}
        definirFiltro={setFiltro}
        corLigada={corLigada}
        definirCor={setCorLigada}
        janela={janela}
        p95={p95}
        pedidosAbertos={pedidosAbertos}
        alternarPedidos={() => setPedidosAbertos((a) => !a)}
      />

      <div
        className="mapa"
        ref={caixa}
        onScroll={recalcular}
        onMouseOver={aoPassar}
        onMouseLeave={aoSair}
        onClick={aoClicar}
      >
        <div
          className="mapa-conteudo"
          style={{ width: larguraTotal, height: alturaTotal }}
          onPointerDown={aoDescerMedida}
          onPointerMove={aoMoverMedida}
          onPointerUp={aoSubirMedida}
          onPointerCancel={aoSubirMedida}
          onDoubleClick={aoDuploCliqueMedida}
        >
          <Cabecalho
            dados={dados}
            janela={janela}
            inicioSemana={inicioSemana}
            offsetsDias={offsetsDias}
          />
          {/* Espaçadores: a altura vem das posições acumuladas e não de uma
              multiplicação, porque as linhas já não têm todas a mesma altura. */}
          <div style={{ height: offsetsLinhas[janela.viatura0] ?? 0 }} />
          {linhas}
          <div
            style={{
              height:
                (offsetsLinhas[dados.viaturas.length] ?? 0) - (offsetsLinhas[janela.viatura1] ?? 0),
            }}
          />
        </div>
        <Puxador
          eixo="x"
          className="puxador puxador--coluna"
          valor={larguraViatura}
          minimo={LARGURA_VIATURA_MIN}
          maximo={LARGURA_VIATURA_MAX}
          variavel="--largura-viatura"
          alvo={envolvente}
          aoFixar={setLarguraViatura}
          rotulo="largura da coluna das viaturas"
        />
      </div>

      {sobre !== null && seleccao === null ? <Cartao dados={dados} posicao={sobre} /> : null}

      <Painel
        dados={dados}
        alvo={fixado}
        fixo={seleccao !== null}
        aberto={painelAberto}
        alternar={() => setPainelAberto((a) => !a)}
        limpar={() => setSeleccao(null)}
        configuracao={configuracao}
        altura={alturaPainel}
        definirAltura={setAlturaPainel}
        envolvente={envolvente}
      />

      {/* 📗 fonte 5.3.3.1 · a fila de trabalho, agora como JANELA sobre o mapa.
          Última na ordem do DOM de propósito: sem `z-index` a competir, a última
          irmã pintada é a que fica em cima — e é a que o utilizador está a usar. */}
      {pedidosAbertos ? (
        <Pedidos
          fechar={() => setPedidosAbertos(false)}
          // O painel mostra o NOME COMPLETO da localidade, como foi pedido: «para além
          // das 3 letras, o nome completo, para não existirem dúvidas».
          nomeLocalidade={(i) => nomeLocal(dados, i)}
          nomeMotorista={(i) => dados.motoristas[i] ?? '—'}
          recarregar={recarregar}
          numeroViatura={(i) => dados.viaturas[i]?.numero ?? String(i)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cabeçalho fixo: mês + dia
// ---------------------------------------------------------------------------

function Cabecalho({
  dados,
  janela,
  inicioSemana,
  offsetsDias,
}: {
  dados: DadosMapa;
  janela: Janela;
  inicioSemana: readonly boolean[];
  offsetsDias: readonly number[];
}) {
  const meses = [];
  const dias = [];

  // Agrupar os dias visíveis por mês: uma célula por mês, não um rótulo por dia.
  let inicio = janela.dia0;
  for (let d = janela.dia0; d < janela.dia1; d++) {
    const mes = dados.mesPorDia[d];
    const seguinte = d + 1 < janela.dia1 ? dados.mesPorDia[d + 1] : -1;
    if (mes !== seguinte) {
      meses.push(
        <div
          key={inicio}
          className="mes"
          // A largura do mês é a soma das larguras dos seus dias, que podem ser
          // diferentes entre si — daí a diferença de posições e não uma multiplicação.
          style={{ width: (offsetsDias[d + 1] ?? 0) - (offsetsDias[inicio] ?? 0) }}
        >
          <span>{MESES[mes ?? 0]}</span>
        </div>,
      );
      inicio = d + 1;
    }
    const data = new Date(Date.UTC(dados.ano, 0, 1 + d));
    dias.push(
      <div
        key={d}
        className={classes(
          'dia-cab',
          dados.fimDeSemana[d] === true && 'dia-cab--fds',
          inicioSemana[d] === true && 'dia-cab--semana',
        )}
        // ⚠️ `data-dia` é o que o arrasto usa para saber QUAL coluna redimensionar.
        // Deduzi-lo de coordenadas seria repetir a busca binária num sítio onde ela
        // pode divergir da que desenha.
        data-dia={d}
        style={{ width: tamanho(offsetsDias, d) }}
      >
        <span className="dia-cab-num">{dados.diaDoMes[d]}</span>
        <span className="dia-cab-sem">{DIAS_SEMANA[data.getUTCDay()]}</span>
      </div>,
    );
  }

  return (
    <div className="mapa-cabecalho">
      <div className="canto">
        <span className="canto-ano">{dados.ano}</span>
        <span className="canto-rot">
          {dados.viaturas.length} viaturas · {dados.nDias} dias
        </span>
      </div>
      <div className="faixas">
        <div className="faixa-cab" style={{ paddingLeft: offsetsDias[janela.dia0] ?? 0 }}>
          {meses}
        </div>
        <div className="faixa-cab" style={{ paddingLeft: offsetsDias[janela.dia0] ?? 0 }}>
          {dias}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cabeçalho da aplicação
// ---------------------------------------------------------------------------

/**
 * Três zonas, e a ordem é a do pedido do cliente: identidade à esquerda, caixas
 * de comando ao centro, medição à direita.
 *
 * O título leva o subtítulo **por baixo** — «Turismo · GVA» era uma etiqueta
 * solta ao lado e lia-se como se fosse outra coisa.
 */
function Topo({
  dados,
  filtro,
  definirFiltro,
  corLigada,
  definirCor,
  janela,
  p95,
  pedidosAbertos,
  alternarPedidos,
}: {
  dados: DadosMapa;
  filtro: Filtro;
  definirFiltro: (f: Filtro) => void;
  corLigada: boolean;
  definirCor: (b: boolean) => void;
  janela: Janela;
  p95: number | null;
  /** ⚠️ O estado da janela de pedidos vive no `Mapa`, porque a janela também. */
  pedidosAbertos: boolean;
  alternarPedidos: () => void;
}) {
  const [aberto, setAberto] = useState<'config' | 'filtro' | 'legenda' | null>(null);
  const nFiltros = contaFiltros(filtro, dados);

  return (
    <header className="topo">
      <div className="topo-marca">
        {/* O logótipo vem do ficheiro do repositório e não de um URL externo: numa
            aplicação que corre no escritório, uma imagem remota é uma dependência
            de rede para mostrar a própria marca. É o mesmo ficheiro. A altura é
            fixada e a largura fica `auto`, para a proporção 1920×889 nunca ser
            distorcida. */}
        <img className="logo" src="/logo-xlvision.png" alt="xlVISION" />
        <span className="separador-v" />
        <span className="topo-titulos">
          <h1 className="titulo">Mapa de planeamento</h1>
          <span className="subtitulo">Turismo · Grupo Vale do Ave</span>
        </span>
      </div>

      <nav className="topo-caixas" aria-label="Comandos do mapa">
        <BotaoMenu
          icone={<IconePaleta em={1} />}
          rotulo="Configuração"
          aberto={aberto === 'config'}
          alternar={() => setAberto((a) => (a === 'config' ? null : 'config'))}
        />
        <BotaoMenu
          icone={<IconePesquisa em={1} />}
          rotulo="Filtros"
          contagem={nFiltros === 0 ? 'nenhum' : `${nFiltros} activo${nFiltros > 1 ? 's' : ''}`}
          aberto={aberto === 'filtro'}
          alternar={() => setAberto((a) => (a === 'filtro' ? null : 'filtro'))}
        />
        <BotaoMenu
          icone={<IconeInfo em={1} />}
          rotulo="Legenda"
          aberto={aberto === 'legenda'}
          alternar={() => setAberto((a) => (a === 'legenda' ? null : 'legenda'))}
        />
        {/* 📗 fonte 5.3.3.1: é daqui que o planeamento começa. O botão fica ao lado dos
            outros e não em destaque, porque o mapa continua a ser o ecrã principal — a
            lista é a fila de trabalho, não a casa.
            ⚠️ O botão está aqui; a JANELA não. Retorno do cliente (2026-08-05): a lista
            era um menu preso à barra, e tinha de ser uma janela que deixa ver o mapa.
            Por isso o estado vive no `Mapa` e não aqui — ver `janela.ts`. */}
        <BotaoMenu
          icone={<IconeLista em={1} />}
          rotulo="Pedidos"
          aberto={pedidosAbertos}
          alternar={alternarPedidos}
        />
      </nav>

      <div className="topo-medidas" role="status">
        <span className="metrica">
          <span className="metrica-valor">
            {((janela.viatura1 - janela.viatura0) * (janela.dia1 - janela.dia0)).toLocaleString(
              'pt-PT',
            )}
          </span>
          <span className="metrica-rot">dias</span>
        </span>
        <span className="separador-v" />
        <span className={classes('metrica', p95 !== null && p95 > 60 && 'metrica--alta')}>
          <span className="metrica-valor">{p95 === null ? '—' : `${p95.toFixed(1)} ms`}</span>
          <span className="metrica-rot">p95</span>
        </span>
      </div>

      {aberto === 'config' ? (
        <section className="menu" aria-label="Configuração">
          <div className="menu-cab">
            <strong className="menu-titulo">Configuração</strong>
            <button
              type="button"
              className="btn btn--icone"
              onClick={() => setAberto(null)}
              aria-label="Fechar"
            >
              <IconeFechar em={1} />
            </button>
          </div>
          <Interruptor ligado={corLigada} definir={definirCor} />
          <p className="menu-nota">
            Com a cor desligada o mapa fica em tons neutros e a <strong>sigla</strong> continua a
            identificar o cliente. Com ~40 clientes a cor não identifica — serve para varrer o mapa.
          </p>
        </section>
      ) : null}
      {aberto === 'filtro' ? (
        <PainelFiltros
          dados={dados}
          filtro={filtro}
          definirFiltro={definirFiltro}
          fechar={() => setAberto(null)}
        />
      ) : null}
      {aberto === 'legenda' ? <Legenda fechar={() => setAberto(null)} /> : null}
    </header>
  );
}

/** Interruptor de dois estados, padronizado. Não é uma caixa de selecção nua. */
function Interruptor({ ligado, definir }: { ligado: boolean; definir: (b: boolean) => void }) {
  return (
    <button
      type="button"
      className={classes('btn', 'btn--comuta', ligado && 'btn--on')}
      onClick={() => definir(!ligado)}
      aria-pressed={ligado}
    >
      <IconePaleta em={1} />
      <span>Cor por cliente</span>
      <span className="pastilha">{ligado ? 'ligada' : 'desligada'}</span>
    </button>
  );
}

function BotaoMenu({
  icone,
  rotulo,
  contagem,
  aberto,
  alternar,
}: {
  icone: React.ReactNode;
  rotulo: string;
  contagem?: string;
  aberto: boolean;
  alternar: () => void;
}) {
  return (
    <button
      type="button"
      className={classes('btn', aberto && 'btn--on')}
      onClick={alternar}
      aria-expanded={aberto}
    >
      {icone}
      <span>{rotulo}</span>
      {contagem !== undefined ? <span className="pastilha">{contagem}</span> : null}
      <IconeChevron em={0.9} aberto={aberto} className="ic ic--fraco" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Filtros — três eixos, porque foram os três que o cliente pediu
// ---------------------------------------------------------------------------

type Eixo = 'clientes' | 'motoristas' | 'rotas';

function PainelFiltros({
  dados,
  filtro,
  definirFiltro,
  fechar,
}: {
  dados: DadosMapa;
  filtro: Filtro;
  definirFiltro: (f: Filtro) => void;
  fechar: () => void;
}) {
  const [eixo, setEixo] = useState<Eixo>('clientes');
  const [procura, setProcura] = useState('');
  const idProcura = useId();

  const itens: readonly { readonly i: number; readonly sigla: string; readonly nome: string }[] =
    eixo === 'clientes'
      ? dados.clientes.map((c, i) => ({ i, sigla: c[0], nome: c[1] }))
      : eixo === 'motoristas'
        ? dados.motoristas.map((m, i) => ({ i, sigla: m, nome: '' }))
        : dados.itinerarios.map((r, i) => ({ i, sigla: r, nome: '' }));

  const total = itens.length;
  const seleccionados = filtro[eixo];
  const visiveis =
    procura === ''
      ? itens
      : itens.filter(
          (x) =>
            x.sigla.toLowerCase().includes(procura.toLowerCase()) ||
            x.nome.toLowerCase().includes(procura.toLowerCase()),
        );

  const pôr = (novo: ReadonlySet<number> | null): void =>
    definirFiltro({ ...filtro, [eixo]: novo });

  const alternar = (i: number): void => {
    const base = seleccionados ?? new Set(itens.map((x) => x.i));
    const n = new Set(base);
    if (n.has(i)) n.delete(i);
    else n.add(i);
    pôr(n.size === total ? null : n);
  };

  return (
    <section className="menu" aria-label="Filtros">
      <div className="menu-cab">
        <div className="abas" role="tablist">
          {(
            [
              ['clientes', 'Clientes', dados.clientes.length],
              ['motoristas', 'Motoristas', dados.motoristas.length],
              ['rotas', 'Itinerários', dados.itinerarios.length],
            ] as const
          ).map(([k, rot, n]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={eixo === k}
              className={classes('aba', eixo === k && 'aba--on')}
              onClick={() => {
                setEixo(k);
                setProcura('');
              }}
            >
              {rot}
              <span className="pastilha">
                {filtro[k] === null ? n.toLocaleString('pt-PT') : `${filtro[k]?.size}/${n}`}
              </span>
            </button>
          ))}
        </div>
        <button type="button" className="btn btn--icone" onClick={fechar} aria-label="Fechar">
          <IconeFechar em={1} />
        </button>
      </div>

      <div className="menu-acoes menu-acoes--esq">
        <label className="procura" htmlFor={idProcura}>
          <IconePesquisa em={1} className="ic ic--fraco" />
          <input
            id={idProcura}
            type="search"
            value={procura}
            onChange={(e) => setProcura(e.target.value)}
            placeholder="Procurar"
          />
        </label>
        <button type="button" className="btn btn--peq" onClick={() => pôr(null)}>
          <IconeVisto em={1} />
          <span>Marcar todos</span>
        </button>
        <button type="button" className="btn btn--peq" onClick={() => pôr(new Set())}>
          <IconeVazio em={1} />
          <span>Desmarcar todos</span>
        </button>
      </div>

      <p className="menu-nota">
        O que se desmarca fica <strong>esbatido</strong>, não escondido — para nunca parecer que a
        viatura está livre quando não está.
      </p>

      <div className="menu-lista">
        {visiveis.map(({ i, sigla, nome }) => {
          const on = activo(seleccionados, i);
          const cor = eixo === 'clientes' ? corCliente(i, 'claro') : null;
          return (
            <button
              key={i}
              type="button"
              className={classes('cliente', on && 'cliente--on')}
              onClick={() => alternar(i)}
              aria-pressed={on}
              title={nome !== '' ? nome : sigla}
            >
              <span className="cliente-caixa">{on ? <IconeVisto em={0.9} /> : null}</span>
              {cor !== null ? (
                <span
                  className="cliente-amostra"
                  style={{ background: cor.fundo, borderLeftColor: cor.cap }}
                />
              ) : (
                <span className="cliente-amostra cliente-amostra--vazia" />
              )}
              <span className="cliente-sigla">{sigla}</span>
              <span className="cliente-nome">{nome}</span>
            </button>
          );
        })}
        {visiveis.length === 0 ? <p className="menu-nota">Nada corresponde.</p> : null}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Legenda — «o mapa fácil de entender» começa por explicar os seus sinais
// ---------------------------------------------------------------------------

function Legenda({ fechar }: { fechar: () => void }) {
  return (
    <section className="menu menu--legenda" aria-label="Legenda">
      <div className="menu-cab">
        <strong className="menu-titulo">Como ler o mapa</strong>
        <button type="button" className="btn btn--icone" onClick={fechar} aria-label="Fechar">
          <IconeFechar em={1} />
        </button>
      </div>

      <div className="legenda-grelha">
        <div className="legenda-bloco">
          <h3>As três bandas de cada viatura</h3>
          <ol className="legenda-bandas">
            <li>
              <span className="amostra amostra--itin">IGT</span>
              <span>
                <strong>Itinerário.</strong> Uma barra por corrida, com a sigla do cliente. A cor é
                para varrer o mapa; a sigla é a identidade.
              </span>
            </li>
            <li>
              <span className="amostra amostra--local">MAD</span>
              <span>
                <strong>Local do dia.</strong> Muda todos os dias. Nos dias sem chegada nem partida é{' '}
                <em>onde o motorista dorme</em>.
              </span>
            </li>
            <li>
              <span className="amostra amostra--mot">Silva</span>
              <span>
                <strong>Motorista.</strong> Barra própria, porque não coincide com a do itinerário:
                a troca a meio parte uma e não a outra.
              </span>
            </li>
          </ol>
        </div>

        <div className="legenda-bloco">
          <h3>Sinais</h3>
          <ul className="legenda-itens">
            <li>
              <span className="amostra amostra--rotacao">
                <IconeRotacao em={1} />
              </span>
              <span>
                <strong>Dia de rotação.</strong> Chega de um itinerário e parte noutro no mesmo dia.
                É onde a margem se ganha.
              </span>
            </li>
            <li>
              <span className="amostra amostra--estado">
                <IconeOficina em={1} />
              </span>
              <span>
                <strong>Viatura indisponível.</strong> Oficina, avaria ou sinistro. Nestes dias o
                motorista está em casa ou noutra viatura.
              </span>
            </li>
            <li>
              <span className="amostra amostra--reserva" />
              <span>
                <strong>Reserva.</strong> Registo ainda não confirmado. É textura e não
                transparência, para se ver também em impressão.
              </span>
            </li>
            <li>
              <span className="amostra amostra--dupla">
                <IconeDupla em={1} />
              </span>
              <span>
                <strong>Tripulação dupla.</strong> Dois condutores em simultâneo — regime legal de
                repouso diferente (art. 8.º n.º 5).
              </span>
            </li>
            {/* ⚠️ Os DOIS `?` do mapa, e estão aqui porque o cliente não os
                percebeu — «não percebi o ? em certos dias». Eram o mesmo glifo
                com dois significados e nenhum deles explicado. Continuam a ser
                `?` porque é o que a folha real escreve (`Rodolfo?`, `PAR in ?`),
                mas agora distinguem-se: um na barra do itinerário, o outro em
                caixa tracejada na banda do local. */}
            <li>
              <span className="amostra amostra--duvida">?</span>
              <span>
                <strong>Provisório.</strong> Na barra do itinerário. O planeador marcou-o como não
                firme — é a interrogação que a folha actual já usa.
              </span>
            </li>
            <li>
              <span className="amostra amostra--falta">?</span>
              <span>
                <strong>Local por definir.</strong> Na banda do local, em caixa tracejada. Falta
                dizer onde a viatura está — e nos dias sem chegada nem partida esse local{' '}
                <em>é onde o motorista dorme</em>: decide se o repouso é legal (art. 8.º n.º 8) e se
                há alojamento a pagar. Medido na folha actual: <strong>5,9 %</strong> dos dias de
                viagem estão em branco.
              </span>
            </li>
            <li>
              <span className="amostra amostra--base">na base</span>
              <span>
                <strong>Sem serviço.</strong> Lisa quando está <em>na base</em> — o motorista vai a
                casa e não há alojamento a pagar. Tracejada quando fica{' '}
                <em>parada fora</em>, que é pernoita paga. Na folha actual esta distinção não existe:
                das 240 células com local em dias sem viagem, nenhuma é em Portugal.
              </span>
            </li>
          </ul>
        </div>

        <div className="legenda-bloco">
          <h3>Calendário e cor</h3>
          <ul className="legenda-itens">
            <li>
              <span className="amostra amostra--fds" />
              <span>
                <strong>Fim de semana.</strong> Como na folha actual, marcado na linha do dia.
              </span>
            </li>
            <li>
              <span className="amostra amostra--semana" />
              <span>
                <strong>Segunda-feira.</strong> Fronteira da semana legal (art. 4.º i)) — é onde os
                contadores de condução reiniciam.
              </span>
            </li>
            <li>
              <span className="amostra amostra--quente" />
              <span>
                <strong>Quente é problema.</strong> Verde é conforme. Frio e púrpura são clientes.
                Nenhuma cor de cliente é quente, para o alarme não ter concorrência.
              </span>
            </li>
          </ul>
          <p className="menu-nota">
            Passe o rato por um dia para o resumo; <strong>clique</strong> para fixar e realçar o
            itinerário inteiro. As divisórias arrastam-se.
            {/* Sem isto ninguém descobre o gesto: a tira tem 6 px e só se revela ao
                passar o rato. Uma funcionalidade que não se encontra não existe. */}
            <br />
            <strong>Como no Excel:</strong> arraste a <em>borda direita</em> de um número de dia para
            alargar <em>essa</em> coluna, ou a <em>borda de baixo</em> de uma viatura para esticar{' '}
            <em>essa</em> linha. As outras não se mexem. <strong>Duplo clique</strong> na borda
            devolve-a à medida original.
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Cartão de resumo, junto ao cursor
// ---------------------------------------------------------------------------

function Cartao({
  dados,
  posicao,
}: {
  dados: DadosMapa;
  posicao: Posicao & { x: number; y: number };
}) {
  const linhas = resumo(dados, posicao.v, posicao.d);
  // Manter o cartão dentro da janela: junto à borda direita, abre para a esquerda.
  const largura = 260;
  const x = Math.min(posicao.x, window.innerWidth - largura - 12);
  const abaixo = posicao.y + 160 < window.innerHeight;
  return (
    <div
      className="cartao"
      style={
        abaixo
          ? { left: x, top: posicao.y + 6 }
          : { left: x, bottom: window.innerHeight - posicao.y + ALTURA_LINHA + 6 }
      }
      role="tooltip"
    >
      {linhas.map((l, i) => (
        <div key={i} className={i === 0 ? 'cartao-cab' : 'cartao-linha'}>
          {l}
        </div>
      ))}
      <div className="cartao-dica">clique para fixar</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Painel de detalhe — fechado por omissão
// ---------------------------------------------------------------------------

function Painel({
  dados,
  alvo,
  fixo,
  aberto,
  alternar,
  limpar,
  configuracao,
  altura,
  definirAltura,
  envolvente,
}: {
  dados: DadosMapa;
  alvo: Posicao | null;
  fixo: boolean;
  aberto: boolean;
  alternar: () => void;
  limpar: () => void;
  configuracao: ConfiguracaoMapa;
  altura: number;
  definirAltura: (n: number) => void;
  envolvente: React.RefObject<HTMLDivElement | null>;
}) {
  const pares =
    alvo === null ? [] : detalhe(dados, alvo.v, alvo.d, configuracao.minutosRotacaoMinimo);
  const resumoCurto =
    alvo === null
      ? 'Passe o rato por um dia; clique para fixar.'
      : pares
          .slice(0, 3)
          .map(([, valor]) => valor)
          .join('  ·  ');

  return (
    <section className={classes('painel', aberto && 'painel--aberto')}>
      {aberto ? (
        <Puxador
          eixo="y"
          className="puxador puxador--painel"
          valor={altura}
          minimo={ALTURA_PAINEL_MIN}
          maximo={ALTURA_PAINEL_MAX}
          variavel="--altura-painel"
          alvo={envolvente}
          aoFixar={definirAltura}
          invertido
          rotulo="altura do painel de detalhe"
        />
      ) : null}

      <div className="painel-cab">
        <button type="button" className="btn btn--peq" onClick={alternar} aria-expanded={aberto}>
          <IconeChevron em={1} aberto={aberto} />
          <span>Detalhe</span>
        </button>
        {fixo ? (
          <span className="pastilha pastilha--fixo">
            fixo
            <button type="button" className="pastilha-x" onClick={limpar} aria-label="largar">
              <IconeFechar em={0.9} />
            </button>
          </span>
        ) : null}
        {!aberto ? <span className="painel-resumo">{resumoCurto}</span> : null}
      </div>

      {aberto ? (
        // `aria-live="polite"` e não `assertive`: quem usa leitor de ecrã não quer
        // ser interrompido a cada célula por que passa o rato.
        <dl className="painel-corpo" aria-live="polite">
          {alvo === null ? (
            <div className="painel-vazio">Passe o rato por um dia; clique para fixar.</div>
          ) : (
            pares.map(([chave, valor]) => (
              <div key={chave} className="par">
                <dt>{chave}</dt>
                <dd>{valor}</dd>
              </div>
            ))
          )}
        </dl>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Puxador de redimensionamento
// ---------------------------------------------------------------------------

/**
 * Arrasta e redimensiona.
 *
 * Durante o arrasto escreve **directamente na variável CSS** e só fixa o estado do
 * React ao largar. Passar por estado a cada movimento do ponteiro voltaria a
 * desenhar as linhas visíveis em cada pixel — e o arrasto é justamente onde isso
 * se sentiria. O layout responde igual porque a variável é a mesma que o CSS usa.
 */
function Puxador({
  eixo,
  className,
  valor,
  minimo,
  maximo,
  variavel,
  alvo,
  aoFixar,
  invertido = false,
  rotulo,
}: {
  eixo: 'x' | 'y';
  className: string;
  valor: number;
  minimo: number;
  maximo: number;
  variavel: string;
  alvo: React.RefObject<HTMLDivElement | null>;
  aoFixar: (n: number) => void;
  invertido?: boolean;
  rotulo: string;
}) {
  const inicio = useRef<{ pos: number; base: number } | null>(null);
  const ultimoValor = useRef(valor);

  const aoDescer = (e: EventoPonteiro<HTMLDivElement>): void => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    inicio.current = { pos: eixo === 'x' ? e.clientX : e.clientY, base: valor };
  };

  const aoMover = (e: EventoPonteiro<HTMLDivElement>): void => {
    const i = inicio.current;
    if (i === null) return;
    const delta = (eixo === 'x' ? e.clientX : e.clientY) - i.pos;
    const bruto = i.base + (invertido ? -delta : delta);
    const novo = Math.max(minimo, Math.min(maximo, bruto));
    alvo.current?.style.setProperty(variavel, `${novo}px`);
    ultimoValor.current = novo;
  };

  const aoSubir = (e: EventoPonteiro<HTMLDivElement>): void => {
    if (inicio.current === null) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    inicio.current = null;
    aoFixar(ultimoValor.current);
  };

  return (
    <div
      className={className}
      role="separator"
      aria-label={rotulo}
      aria-orientation={eixo === 'x' ? 'vertical' : 'horizontal'}
      onPointerDown={aoDescer}
      onPointerMove={aoMover}
      onPointerUp={aoSubir}
      onPointerCancel={aoSubir}
    />
  );
}

// ---------------------------------------------------------------------------

/**
 * Tema activo, seguido em tempo real.
 *
 * A cor das barras é calculada em JavaScript (depende do cliente), logo mudar de
 * tema no sistema tem de chegar aqui. Sem isto, quem alterna o tema fica com um
 * mapa de cores erradas até recarregar.
 */
function useTema(): Tema {
  const [tema, setTema] = useState<Tema>(() =>
    typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
      ? 'escuro'
      : 'claro',
  );

  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const consulta = matchMedia('(prefers-color-scheme: dark)');
    const aoMudar = (): void => setTema(consulta.matches ? 'escuro' : 'claro');
    consulta.addEventListener('change', aoMudar);
    return () => consulta.removeEventListener('change', aoMudar);
  }, []);

  return tema;
}
